// ts-rest contract for the firm document library (SEP-256 request 4).
//
// THE FIRM HOLDS ITS OWN LITERATURE, AND THE SERVICE TYPE DECIDES THE PACK.
// Ops publish the file, and they tag it with the service types that carry it.
// The investment manager who records a send then picks the pack of the service
// type in one press, and opens the same file that the firm published.
//
// The live slot is the pair (document code, service type), so one document can
// hold a different file for each pack and the list can hold a code twice.
//
// Two JSON doors sit here: the read that every staff persona uses, and the
// retire that only Ops, Compliance and admin reach. The PUBLISH is multipart,
// so it is a raw Nest route beside them and not a ts-rest entry: ts-rest is
// JSON-only, and the client Documents door sets that precedent. The bytes come
// back from a binary route for the same reason.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { packAccountTypeSchema, packServiceTypeSchema } from '../onboarding-document-registers';
import { errorBody } from '../schemas/common';

const c = initContract();

/** One live item of the firm library, as the vault and the send picker read it. */
export const firmDocumentSchema = z.object({
    id: z.string(),
    /** The catalogue code, or the slug the firm minted. A value, so snake_case. */
    documentCode: z.string(),
    title: z.string(),
    /**
     * TRUE when `PROVIDED_DOCUMENT_CATALOGUE` holds the code. The catalogue
     * stays code, because the final approval gate reads `mandatory` from it, so
     * the vault states which of its rows the catalogue governs.
     */
    catalogueItem: z.boolean(),
    /** What the catalogue says. A document the firm added is never mandatory. */
    mandatory: z.boolean(),
    /**
     * The packs that carry THIS FILE of the document. An empty list is legal.
     *
     * The live slot of the vault is the pair (document code, service type), so
     * one code can hold a different file for each pack. The rows of one code
     * carry disjoint service types, and the publish Action holds that rule, so
     * a pack still reads one file for one document.
     */
    serviceTypes: z.array(packServiceTypeSchema),
    /**
     * The account types whose pack carries this file (SEP-346). An empty list
     * means every account type.
     */
    accountTypes: z.array(packAccountTypeSchema),
    /** The live label of `FirmDocumentVersion`, or null while the firm holds none. */
    versionLabel: z.string().nullable(),
    /** The plain calendar day that version takes effect, `YYYY-MM-DD`. */
    effectiveFrom: z.string().nullable(),
    /** TRUE when the vault holds bytes for the row. */
    hasFile: z.boolean(),
    filename: z.string().nullable(),
    publishedAt: z.string(),
    publishedByUserId: z.string().nullable(),
    /** The directory name of the publisher, or null when the id resolves to none. */
    publishedByName: z.string().nullable(),
});
export type FirmDocumentDto = z.infer<typeof firmDocumentSchema>;

export const firmDocumentsResponseSchema = z.object({
    /**
     * ONE ROW FOR EACH LIVE (CODE, PACK) SLOT, so a code can appear more than
     * once: Ops hold one Risk and Investment Guide for the Discretionary and
     * the Advisory pack, and another for Execution Only. A superseded row and a
     * retired row are both absent: a pack must never offer a file the firm
     * stopped giving.
     */
    documents: z.array(firmDocumentSchema),
});
export type FirmDocumentsResponse = z.infer<typeof firmDocumentsResponseSchema>;

export const firmDocumentsContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/firm-documents',
            responses: { 200: firmDocumentsResponseSchema, 403: errorBody },
            summary:
                'The live client literature of the firm, with the service types whose pack carries each item (SEP-256 request 4)',
        },
        retire: {
            method: 'POST',
            path: '/firm-documents/:id/retire',
            body: z.object({}),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
            },
            summary:
                'Take one file of client literature out of the packs it carried. The row survives, retired (SEP-256 request 4)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
